import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { Plus } from '@bloo-team/ui/icons'
import { isUserActive } from '@bloo-team/api-client'
import type { PlatformUser } from '@bloo-team/api-client'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { Pagination } from '@bloo-team/ui'
import { DirectorySync } from '../../../components/settings/DirectorySync'
import { UserForm, type UserFormMode } from '../../../components/settings/UserForm'
import { UserTable } from '../../../components/settings/UserTable'
import {
  canConfirm, confirmPrompt, confirmVerb, successMessage,
  type OrgLookup, type UserAction, type UserConfirm,
} from '../../../lib/user-confirm'
import { useSession } from '../../../lib/session'
import {
  useCreateUser,
  useDeleteUser,
  useResetMfa,
  useResetPassword,
  useSetUserStatus,
  useUpdateUser,
  useUserDetail,
  useUserTypes,
  usePlatformUsers,
} from '../../../lib/platform-users'
// The organization directory and directory sign-in are their own modules: one
// belongs to Organizations and one is configured per organization. Users is
// their consumer, not their owner.
import { useOrganizations, useOrgResolution } from '../../../lib/organizations'
import {
  useAdConfig,
  useAdSyncRunning,
  useSaveAdConfig,
  useSetAdEnabled,
  useStartAdSync,
} from '../../../lib/directory-sync'

/**
 * Settings · Users (docs/settings-requirements.md §5).
 *
 * Who can sign in, across organizations — not the scope assignee directory,
 * which is a different endpoint entirely.
 *
 * A user row carries `orgName` and no id, but two of the three writes need an
 * id IN THEIR PATH. Each resolves it differently, and none guesses:
 *
 *   create  the operator PICKS the organization, so the id comes from the
 *           selection.
 *   edit    needs no organization at all — PUT /lc/mgmt/users/{userId}.
 *   delete  the organization is looked up by an EXACT name query at the moment
 *           the confirm opens, and the confirm names the organization it will
 *           address. No match, or an ambiguous one, refuses rather than
 *           choosing — a wrong-but-real org id is a delete whose target is
 *           unknown (G10).
 *
 * Two contract traps, both handled in the api-client and both invisible here
 * by design: a row's status reads `active`/`inactive` while the write takes
 * `enabled`/`disabled`, and `last_login` is epoch SECONDS where 0 means never.
 */

function UsersPage() {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [confirm, setConfirm] = useState<UserConfirm | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [form, setForm] = useState<{ mode: UserFormMode; user: PlatformUser | null } | null>(null)
  const [orgQuery, setOrgQuery] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [adOrg, setAdOrg] = useState<string | null>(null)

  // Server-side search: settle before asking.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 250)
    return () => clearTimeout(t)
  }, [search])

  // The create picker's own search, debounced for the same reason.
  useEffect(() => {
    const t = setTimeout(() => setOrgSearch(orgQuery), 250)
    return () => clearTimeout(t)
  }, [orgQuery])

  const session = useSession()
  const deployment = useMemo(
    () => (session?.clusters ?? []).map((c) => ({
      clusterId: c.clusterId, clusterName: c.clusterName, scopes: c.scopes,
    })),
    [session],
  )

  const list = usePlatformUsers({ pageNo, pageSize, query })
  const userTypes = useUserTypes()
  const setStatus = useSetUserStatus()
  const resetPassword = useResetPassword()
  const resetMfa = useResetMfa()
  const create = useCreateUser()
  const update = useUpdateUser()
  const remove = useDeleteUser()

  // TWO directory reads, deliberately not one. The create picker is a live
  // search; the directory panel wants a stable list. Sharing a query would let
  // typing in the create sheet mutate the organization dropdown behind it.
  const orgPicker = useOrganizations(orgSearch, form?.mode === 'create')
  const orgList = useOrganizations('')
  const detail = useUserDetail(form?.mode === 'edit' ? (form.user?.userId ?? null) : null)

  // Resolved at the moment the delete confirm opens, by an exact name query.
  const orgFor = useOrgResolution(confirm?.action === 'delete' ? confirm.user.orgName : null)
  const resolution = orgFor.resolution
  const orgLookup: OrgLookup = {
    resolution,
    truncated: orgFor.truncated,
    loading: orgFor.isLoading,
  }

  const ad = useAdConfig(adOrg)
  const adSyncing = useAdSyncRunning(Boolean(adOrg && ad.data?.enabled))
  const saveAd = useSaveAdConfig()
  const toggleAd = useSetAdEnabled()
  const startSync = useStartAdSync()

  const rows = list.data?.users ?? []
  const total = list.data?.total ?? 0
  const busy = setStatus.isPending || resetPassword.isPending || resetMfa.isPending || remove.isPending
  const actionError = setStatus.error ?? resetPassword.error ?? resetMfa.error ?? remove.error
  // A confirmation from the LAST action must not sit beside this one's
  // failure: on credential actions, "reset was sent" next to a red error is
  // genuinely ambiguous about which one happened.
  const confirmation = actionError ? null : done

  const onSearch = (value: string) => {
    setSearch(value)
    setPageNo(1)
  }

  const open = (user: PlatformUser, action: UserAction) => {
    setStatus.reset()
    resetPassword.reset()
    resetMfa.reset()
    remove.reset()
    setDone(null)
    setConfirm({ user, action })
  }

  const openForm = (mode: UserFormMode, user: PlatformUser | null) => {
    create.reset()
    update.reset()
    setOrgQuery('')
    setOrgSearch('')
    setForm({ mode, user })
  }

  const run = () => {
    if (!confirm) return
    // Delete is the one action that can be refused before it is attempted, and
    // `canConfirm` is the single place that decides so — the button's disabled
    // state and this guard must not be able to disagree.
    if (!canConfirm(confirm, orgLookup)) return
    const { user, action } = confirm
    const after = { onSuccess: () => { setDone(successMessage(confirm, orgLookup)); setConfirm(null) } }
    if (action === 'delete') {
      // Only ever with an id that resolved to exactly one organization.
      const org = resolution?.kind === 'resolved' ? resolution.org : null
      if (!org) return
      remove.mutate({ userId: user.userId, orgId: org.orgId }, after)
    } else if (action === 'status') {
      setStatus.mutate(user, after)
    } else if (action === 'password') {
      resetPassword.mutate(user, after)
    } else {
      resetMfa.mutate(user, after)
    }
  }

  const prompt = confirmPrompt(confirm, orgLookup)

  return (
    <div className="app-stack app-stack-wide">
      <section className="hud-panel sy-card" aria-label="Users">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Users</span>
          <span className="mono muted">{total}</span>
          <Button variant="outline" size="sm" onPress={() => openForm('create', null)}>
            <Icon icon={Plus} size={13} /> Add user
          </Button>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search users"
            value={search}
            onChange={onSearch}
            placeholder="Search name or email…"
          />
        </div>

        {list.error ? (
          <p className="sy-error" role="alert">{list.error.message}</p>
        ) : null}

        {!rows.length && !list.isLoading && !list.error ? (
          <p className="sy-help">
            {query ? `No user matches “${query}”.` : 'No users in this deployment.'}
          </p>
        ) : null}

        {rows.length ? (
          <UserTable
            users={rows}
            busy={busy}
            onAction={open}
            onEdit={(u) => openForm('edit', u)}
          />
        ) : null}

        {total > 0 ? (
          <Pagination
            label="User pagination"
            page={pageNo}
            pageSize={pageSize}
            total={total}
            busy={list.isFetching}
            unit="users"
            onPageChange={setPageNo}
            onPageSizeChange={(n) => { setPageSize(n); setPageNo(1) }}
          />
        ) : null}

        {confirmation ? <p className="sy-test sy-test-ok" role="status">{confirmation}</p> : null}
        {actionError ? <p className="sy-error" role="alert">{actionError.message}</p> : null}

        {confirm ? (
          <ConfirmStrip
            label={prompt}
            message={prompt}
            confirmLabel={confirmVerb(confirm)}
            tone={
              confirm.action === 'delete' ||
              (confirm.action === 'status' && isUserActive(confirm.user))
                ? 'warn'
                : undefined
            }
            busy={busy}
            // A delete whose organization did not resolve cannot proceed at
            // all — the button is not merely discouraged, it is refused.
            confirmDisabled={!canConfirm(confirm, orgLookup)}
            onCancel={() => setConfirm(null)}
            onConfirm={run}
          />
        ) : null}

        <p className="sy-help">
          Search matches a user's name and email. What the server matches it against is not
          documented, so live results may differ.
        </p>
        <p className="sy-help">
          Removing a user needs their organization, which a row does not carry — it is looked up by
          name when you confirm, and refused if that name is ambiguous. Single sign-on is
          configured per organization and lives on the Organizations screen.
        </p>
      </section>

      <DirectorySync
        orgs={orgList.data?.orgs ?? []}
        orgName={adOrg}
        onOrgName={setAdOrg}
        config={ad.data ?? null}
        loading={ad.isLoading}
        busy={saveAd.isPending || toggleAd.isPending || startSync.isPending}
        syncRunning={adSyncing.data === true}
        error={(saveAd.error ?? toggleAd.error ?? startSync.error ?? ad.error)?.message ?? null}
        onSave={(values) => adOrg && saveAd.mutate({ orgName: adOrg, values })}
        onToggle={(enabled) => adOrg && toggleAd.mutate({ orgName: adOrg, enabled })}
        onSync={() => adOrg && startSync.mutate(adOrg)}
      />

      <UserForm
        open={form !== null}
        mode={form?.mode ?? 'create'}
        detail={form?.mode === 'edit' ? (detail.data ?? null) : null}
        orgs={orgPicker.data?.orgs ?? []}
        orgQuery={orgQuery}
        onOrgQuery={setOrgQuery}
        orgsLoading={orgPicker.isLoading}
        deployment={deployment}
        userTypes={userTypes.data ?? []}
        loading={form?.mode === 'edit' && detail.isLoading}
        busy={create.isPending || update.isPending}
        error={(create.error ?? update.error)?.message ?? null}
        onClose={() => setForm(null)}
        onCreate={(input) =>
          create.mutate(input, {
            onSuccess: () => {
              setForm(null)
              setDone(`${input.name} was added.`)
            },
          })
        }
        onUpdate={(input) =>
          update.mutate(input, {
            onSuccess: () => {
              setForm(null)
              setDone(`${input.name} was updated.`)
            },
          })
        }
      />
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/users')({
  component: UsersPage,
})
